import { AnimatePresence, MotionConfig, useAnimation, useMotionValue } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/Modals/Core';
import useTWScreens from '@/hooks/ui/useTWScreens';
import Overlay from './components/Overlay';
import SidebarContainer from './components/SidebarContainer';
import { SidebarModalProvider } from './context/SidebarModalContext';
import type { CardPlanToggleValue } from '@/components/Modals/SignUp/components/CardPlanToggle';
import { MOTION_TRANSITION } from '@/constants/motion';
interface SidebarModalProps {
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  trigger?: React.ReactNode;
}
const SidebarModal = ({
  trigger,
  children,
  open,
  onOpenChange
}: SidebarModalProps) => {
  const useInternalState = open === undefined || onOpenChange === undefined;
  const [internalOpen, internalOnOpenChange] = useState(false);
  const _open = useInternalState ? internalOpen : open;
  const _onOpenChange = useInternalState ? internalOnOpenChange : onOpenChange;
  const {
    isSm
  } = useTWScreens();

  // Control for animating the sidebar
  const controls = useAnimation();
  const x = useMotionValue(0); // Position of the sidebar

  const handleCloseSidebar = (velocity = 0) => {
    void controls.start({
      x: '100%',
      transition: {
        duration: Math.min(0.5, Math.max(0.1, Math.abs(velocity / 1000))),
        ease: 'linear'
      }
    }).then(() => {
      _onOpenChange(false);
      x.set(0); // Reset the position after closing
    });
  };
  useEffect(() => {
    if (_open) {
      void controls.start({
        x: 0
      }); // Open the sidebar
    }
  }, [_open, controls]);
  const sidebarModalContextValue = useMemo(() => ({
    open: _open,
    onOpenChange: _onOpenChange,
    cardPlanToggleValue: 'creator' as CardPlanToggleValue,
    setCardPlanToggleValue: () => {}
  }), [_open, _onOpenChange]);
  return <SidebarModalProvider value={sidebarModalContextValue} data-sentry-element="SidebarModalProvider" data-sentry-component="SidebarModal" data-sentry-source-file="index.tsx">
      <Modal.Root onOpenChange={_onOpenChange} open={_open} data-sentry-element="Modal.Root" data-sentry-source-file="index.tsx">
        {!!trigger && <Modal.Trigger asChild>{trigger}</Modal.Trigger>}
        <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="index.tsx">
          {_open && <Modal.Portal forceMount>
              <MotionConfig reducedMotion="user" transition={MOTION_TRANSITION}>
                <Overlay />
                <SidebarContainer handleCloseSidebar={handleCloseSidebar} interactive={!isSm}>
                  {children}
                </SidebarContainer>
              </MotionConfig>
            </Modal.Portal>}
        </AnimatePresence>
      </Modal.Root>
    </SidebarModalProvider>;
};
export default SidebarModal;